import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { BASE_URL } from '../services/ApiService/ApiConfig';
import { tokenActual } from './sesionCliente';

/**
 * Activacion del segundo factor (ADR-002 S4): QR para Google o Microsoft Authenticator, codigo de
 * confirmacion y codigos de recuperacion que se muestran una sola vez.
 */
const ActivarSegundoFactor = ({ alTerminar, obligatorio }) => {
    const [paso, setPaso] = useState('inicio');
    const [qr, setQr] = useState(null);
    const [secreto, setSecreto] = useState('');
    const [codigo, setCodigo] = useState('');
    const [codigos, setCodigos] = useState([]);
    const [error, setError] = useState('');
    const [ocupado, setOcupado] = useState(false);

    const llamar = async (ruta, cuerpo) => {
        const res = await fetch(`${BASE_URL}/seguridad/mi/${ruta}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokenActual()}` },
            body: JSON.stringify(cuerpo || {})
        });
        const datos = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(datos.mensaje || 'No se pudo completar. Intente de nuevo.');
        return datos;
    };

    const iniciar = async () => {
        setError('');
        setOcupado(true);
        try {
            const r = await llamar('segundo-factor/iniciar');
            setQr(r.qr);
            setSecreto(r.secreto);
            setPaso('qr');
        } catch (e) {
            setError(e.message);
        } finally {
            setOcupado(false);
        }
    };

    const confirmar = async () => {
        setError('');
        setOcupado(true);
        try {
            const r = await llamar('segundo-factor/confirmar', { codigo });
            setCodigos(r.codigosRecuperacion || []);
            setPaso('codigos');
        } catch (e) {
            setError(e.message);
        } finally {
            setOcupado(false);
        }
    };

    return (
        <div className="flex flex-column gap-3" style={{ maxWidth: '26rem', textAlign: 'left' }}>
            {paso === 'inicio' && (
                <>
                    <p className="m-0">
                        {obligatorio
                            ? 'Su usuario requiere un segundo factor para ingresar a OPEM. Se activa una sola vez.'
                            : 'Proteja su cuenta: ademas de la contrasena, OPEM le pedira un codigo de su celular.'}
                    </p>
                    <ol className="m-0 pl-4 line-height-3">
                        <li>Instale Google Authenticator o Microsoft Authenticator en su celular.</li>
                        <li>Escanee el codigo QR que aparece en el siguiente paso.</li>
                        <li>Escriba el codigo de 6 digitos que muestra la aplicacion.</li>
                    </ol>
                    <Button label="Continuar" icon="pi pi-qrcode" onClick={iniciar} loading={ocupado} />
                </>
            )}
            {paso === 'qr' && (
                <>
                    <p className="m-0">Escanee este codigo con la aplicacion autenticadora:</p>
                    <img src={qr} alt="Codigo QR para la aplicacion autenticadora" style={{ width: '14rem', alignSelf: 'center' }} />
                    <small className="text-600">
                        Si no puede escanear, ingrese esta clave en la aplicacion: <b style={{ fontFamily: 'monospace' }}>{secreto}</b>
                    </small>
                    <label htmlFor="codigo-confirmacion">Codigo de 6 digitos</label>
                    <InputText id="codigo-confirmacion" value={codigo} inputMode="numeric" autoComplete="one-time-code"
                        maxLength={6} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
                        onKeyDown={(e) => { if (e.key === 'Enter' && codigo.length === 6) confirmar(); }} />
                    <Button label="Activar" icon="pi pi-check" onClick={confirmar} disabled={codigo.length !== 6} loading={ocupado} />
                </>
            )}
            {paso === 'codigos' && (
                <>
                    <Message severity="success" text="Segundo factor activado" />
                    <p className="m-0">
                        Guarde estos codigos de recuperacion en un lugar seguro. Cada uno sirve <b>una sola vez</b> para
                        ingresar si pierde el celular. No se volveran a mostrar.
                    </p>
                    <div className="grid" style={{ fontFamily: 'monospace', fontSize: '1.05rem' }}>
                        {codigos.map((c) => <div key={c} className="col-6">{c}</div>)}
                    </div>
                    <Button label="Ya los guarde" icon="pi pi-arrow-right" onClick={() => alTerminar && alTerminar()} />
                </>
            )}
            {error && <Message severity="error" text={error} />}
        </div>
    );
};

export default ActivarSegundoFactor;
